import {
  Form,
  Input,
  Select,
  Radio,
  Modal,
  ModalProps,
  FormProps,
  InputNumber,
  useSelect,
  Checkbox,
  Tooltip,
  Typography,
  message,
} from "@pankod/refine-antd";
import { HttpError, useCustom, useLogout } from "@pankod/refine-core";

import { useAuth } from "definitions";
import { RoleTypes, TagTypes } from "interfaces/enums";
import { Tags } from "interfaces";
import ENV from "utils/env";
import { useState, useEffect } from "react";
import { normalizeTagValues } from "./normalize";

const { Text } = Typography;

type Props = {
  modalProps: ModalProps;
  formProps: FormProps & { initialValues?: any };
  /** Listeden gelen kayıt — stok politikası bu kayıttan okunur. */
  tag?: Tags.TagItem;
  /** Politika kaydedildiğinde listenin tazelenmesi için. */
  onStockPolicySaved?: () => void;
};

interface StockPolicyResponse {
  id: string;
  name: string;
  hideOutOfStock: boolean;
}

/** API bu alanı `{ [key: string]: string }` olarak sakladığı için değer `"true"` gelir. */
const readHideOutOfStock = (record?: {
  superCategories?: { [key: string]: string } | null;
}) => record?.superCategories?.hideOutOfStock === "true";

export const TagsEdit: React.FC<Props> = ({
  formProps,
  modalProps,
  tag,
  onStockPolicySaved,
}) => {
  const [isSuperCategorySelected, setIsSuperCategorySelected] =
    useState<boolean>(!!formProps.initialValues?.superCategory?.id);

  const { selectProps: superCategorySelectProps } = useSelect({
    resource: "super-category",
    optionLabel: "name",
    optionValue: "id",
    defaultValue: formProps.initialValues?.superCategory?.id,
  });

  useEffect(() => {
    if (formProps.initialValues?.superCategory?.id) {
      setIsSuperCategorySelected(true);
    } else {
      setIsSuperCategorySelected(false);
    }
  }, [formProps.initialValues?.superCategory?.id]);

  const isAuthorized = useAuth();
  const { mutate: logout } = useLogout();

  const canEditStockPolicy = isAuthorized(
    RoleTypes.SYSTEM_ADMIN,
    RoleTypes.SALES_MANAGER,
    RoleTypes.SHOP_MANAGER,
    RoleTypes.ASSET_MANAGER
  );

  const tagId: string | undefined = tag?.id ?? formProps.initialValues?.id;
  const serverHideOutOfStock = readHideOutOfStock(
    tag ?? formProps.initialValues
  );

  // Kutunun ekrandaki (henüz kaydedilmemiş) değeri; sunucudaki değer
  // `serverHideOutOfStock`. İkisi farklıysa Kaydet'te politika isteği atılır.
  const [hideOutOfStock, setHideOutOfStock] =
    useState<boolean>(serverHideOutOfStock);

  const isStockPolicyDirty = hideOutOfStock !== serverHideOutOfStock;

  // Modal her açılışta kutuyu sunucudaki değere sabitler; İptal'den sonra
  // kaydedilmemiş tik ekranda kalmasın.
  useEffect(() => {
    setHideOutOfStock(serverHideOutOfStock);
  }, [tagId, serverHideOutOfStock, modalProps.visible]);

  const { refetch: saveStockPolicy, isFetching: stockPolicyIsFetching } =
    useCustom<
      StockPolicyResponse,
      HttpError,
      unknown,
      { hideOutOfStock: boolean }
    >({
      url: `${ENV.API_URL}/api/tags/${tagId}/stock-policy`,
      method: "patch",
      config: {
        payload: {
          hideOutOfStock,
        },
      },
      errorNotification: false,
      // Kaydet akışının parçası olduğu için ayrı başarı bildirimi göstermiyoruz;
      // formun kendi "güncellendi" bildirimi yeterli.
      successNotification: false,
      queryOptions: {
        enabled: false,
        retry: false,
        // Payload query key'in parçası; aç/kapat iki ayrı key arasında gidip
        // geldiği için cache tutmuyoruz, her kaydetme taze istek atsın.
        cacheTime: 0,
        onSuccess: (response) => {
          // Cevaptaki değer gerçek boolean; kutuyu bununla kesinleştir.
          setHideOutOfStock(!!response?.data?.hideOutOfStock);
          onStockPolicySaved?.();
        },
        onError: (error) => {
          if (error.statusCode === 401) {
            logout();
            return;
          }

          if (error.statusCode === 403) {
            message.error("Bu işlem için yetkiniz yok.");
            return;
          }

          if (error.statusCode === 404) {
            message.error("Etiket bulunamadı, liste tazeleniyor.");
            onStockPolicySaved?.();
            return;
          }

          message.error(
            "Stok politikası güncellenemedi, lütfen tekrar deneyin."
          );
        },
      },
    });

  // Stok politikası ayrı bir endpoint'te durduğu için Kaydet'te iki istek olur:
  // önce politika, ardından etiketin kendisi. Politika kaydedilemezse etiket de
  // güncellenmez; modal açık kalır, kullanıcı tekrar deneyebilir.
  const onFinish = async (values: any) => {
    if (isStockPolicyDirty) {
      const result = await saveStockPolicy();

      if (result.isError) {
        return;
      }
    }

    return formProps.onFinish?.(normalizeTagValues(values));
  };

  return (
    <Modal {...modalProps} title="Etiket Düzenle" width={400}>
      <Form
        {...formProps}
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          name: formProps.initialValues?.name,
          description: formProps.initialValues?.description,
          type: formProps.initialValues?.type,
          isActive: formProps.initialValues?.isActive ?? true,
          superCategoryOrder: formProps.initialValues?.superCategoryOrder,
          superCategory: {
            id: formProps.initialValues?.superCategory?.id,
          },
        }}
      >
        <Form.Item
          label="Etiket Adı"
          name="name"
          validateTrigger="onBlur"
          rules={[
            {
              required: true,
              message: "Etiket adı boş olamaz.",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Açıklama"
          name="description"
          validateTrigger="onBlur"
          rules={[
            {
              required: true,
              message: "Etiket açıklaması boş olamaz.",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Süper Kategori (2. seviye)"
          name={["superCategory", "id"]}
          validateTrigger="onBlur"
        >
          <Select
            {...superCategorySelectProps}
            allowClear
            placeholder="Süper Kategori Seçin"
            onChange={(value) => setIsSuperCategorySelected(!!value)}
          />
        </Form.Item>

        {isSuperCategorySelected && (
          <Form.Item
            label="Süper Kategori Gösterim Sırası"
            name="superCategoryOrder"
            validateTrigger="onBlur"
            extra="Boş bırakılırsa etiket, sırası olanların ardından listelenir."
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        )}

        <Form.Item
          label="Etiket Tipi"
          name="type"
          validateTrigger="onBlur"
          rules={[
            {
              required: true,
              message: "Etiket tipi boş olamaz.",
            },
          ]}
        >
          <Select
            options={[
              {
                value: TagTypes.ASSET,
                label: "Asset",
              },
              {
                value: TagTypes.TEMPLATE,
                label: "Template",
              },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Durumu"
          name="isActive"
          rules={[
            {
              required: true,
              message: "Etiket durumunu seçiniz.",
            },
          ]}
        >
          <Radio.Group
            options={[
              {
                label: "Aktif",
                value: true,
              },
              {
                label: "Pasif",
                value: false,
              },
            ]}
          />
        </Form.Item>

        {/*
          Kasıtlı olarak `name` verilmedi: bu ayar etiket gövdesiyle değil
          PATCH /tags/:id/stock-policy ile kaydedilir (bkz. onFinish),
          form gövdesine girmemeli.
        */}
        <Form.Item label="Stok Politikası" style={{ marginBottom: 0 }}>
          <Tooltip
            title={
              canEditStockPolicy ? undefined : "Bu işlem için yetkiniz yok"
            }
          >
            <Checkbox
              checked={hideOutOfStock}
              disabled={!canEditStockPolicy || !tagId || stockPolicyIsFetching}
              onChange={(e) => setHideOutOfStock(e.target.checked)}
            >
              Stoğu olmayan ürünleri gizle
            </Checkbox>
          </Tooltip>
          <Text
            type="secondary"
            style={{ display: "block", marginTop: 4, fontSize: 12 }}
          >
            İşaretlendiğinde, bu kategorideki ürünler stoğu olmayan mağazaların
            katalog ekranlarında görünmez. Ürünler pasife alınmaz; stok
            geldiğinde otomatik olarak geri döner. Kaydet'e bastığınızda kiosk
            ekranlarına anında yansır.
          </Text>
        </Form.Item>
      </Form>
    </Modal>
  );
};
